import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import FormNumberField from './FormNumberField';

interface InflationRateFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
}

const MIN_INFLATION_RATE = 0.01; // 1% is the minimum inflation rate
const MAX_INFLATION_RATE = 0.2; // 20% is the maximum inflation rate

const isInvalidValue = (value: number) => {
  return isNaN(value) || value < MIN_INFLATION_RATE || value > MAX_INFLATION_RATE;
};

/**
 * Wrapper around CustomFormNumberField with specific inflation rate settings.
 *
 * @param props {InflationRateFieldProps}
 */
const InflationRateField: React.FC<InflationRateFieldProps> = (props: InflationRateFieldProps) => {
  const { t } = useTranslation();
  const { name, ...rest } = props;
  const { setFieldValue, getFieldProps } = useFormikContext();

  // Fetch the stored decimal value from Formik
  const fieldValue = parseFloat(getFieldProps(name).value);

  const handleValueChange = (values: { floatValue: number | undefined }) => {
    const { floatValue } = values;
    // Convert the entered percentage back to a decimal for form state
    setFieldValue(name, floatValue ? floatValue / 100 : 0);
  };

  return (
    <FormNumberField
      {...rest}
      name={name}
      endAdornment={'%'}
      tooltipContent={<>{t('general_inflation_minmax_range')}</>}
      error={isInvalidValue(fieldValue)}
      helperText={isInvalidValue(fieldValue) ? t('general_inflation_minmax_range') : ''}
      value={fieldValue * 100} // Convert decimal to percentage for display
      numberFormatProps={{
        percentage: true,
        allowLeadingZeros: false,
        decimalScale: 1,
        decimalSeparator: ',',
        fixedDecimalScale: true,
        allowedDecimalSeparators: ['.', ','],
        allowNegative: false,
        maxLength: 5,
        onValueChange: handleValueChange, // Custom handler for value changes
      }}
    />
  );
};

export default InflationRateField;
